import { useEffect, useMemo, useState } from 'react';
import { RepeatIcon, SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Paginate from 'components/paginate/Paginate';
import { ErrorAlert } from 'components/shared/ErrorAlert';
import { LoadingCard } from 'components/shared/LoadingCard';
import MVRStatusBadge from 'components/mvr/MVRStatusBadge';
import SortableTh from 'components/table/SortableTh';
import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import type {
  MVRReportListItem,
  MVRProductType,
  MVRRequestTransactionType,
} from 'api';
import MvrDownloadButton from './MvrDownloadButton';

const TRANSACTION_TYPE_LABELS: Record<MVRRequestTransactionType, string> = {
  PLPC: 'PL Policy Change',
  CLNB: 'CL New Business',
  CLPC: 'CL Policy Change',
};

const TRANSACTION_TYPE_OPTIONS = Object.entries(TRANSACTION_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const PRODUCT_TYPE_LABELS: Record<MVRProductType, string> = {
  irca: 'IRCA',
  fleet: 'Fleet',
  garage: 'Garage',
};

interface MvrRequestReportListProps {
  reports: MVRReportListItem[];
  count: number;
  isLoading: boolean;
  isRefreshing: boolean;
  error: unknown;
  onRefresh: () => void;
  page: number;
  onSetPage: (page: number) => void;
  getFilter: (key: string) => string;
  setFilter: (key: string, value: string) => void;
  setFilters: (updates: Record<string, string>) => void;
  setFiltersAndPage: (updates: Record<string, string>, pageNum: number) => void;
  onResetFilters: () => void;
}

const MvrRequestReportList = ({
  reports,
  count,
  isLoading,
  isRefreshing,
  error,
  onRefresh,
  page,
  onSetPage,
  getFilter,
  setFilter,
  setFilters,
  setFiltersAndPage,
  onResetFilters,
}: MvrRequestReportListProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const [driverInputValue, setDriverInputValue] = useState(getFilter('driver'));
  const [carrierInputValue, setCarrierInputValue] = useState(
    getFilter('carrier'),
  );
  const [requestedByInputValue, setRequestedByInputValue] = useState(
    getFilter('requested_by'),
  );

  useEffect(() => {
    setDriverInputValue(getFilter('driver'));
    setCarrierInputValue(getFilter('carrier'));
    setRequestedByInputValue(getFilter('requested_by'));
  }, [getFilter]);

  const transactionTypeFilter = getFilter('transaction_type');
  const dateFromFilter = getFilter('date_from');
  const dateToFilter = getFilter('date_to');
  const dateFrom = useMemo(
    () => getDateFromString(dateFromFilter),
    [dateFromFilter],
  );
  const dateTo = useMemo(() => getDateFromString(dateToFilter), [dateToFilter]);
  const sortDirection = (
    getFilter('sort_direction') === 'asc' ? 'asc' : 'desc'
  ) as 'asc' | 'desc';

  const handleSearch = () => {
    setFilters({
      driver: driverInputValue.trim(),
      carrier: carrierInputValue.trim(),
      requested_by: requestedByInputValue.trim(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleSortDirection = () => {
    setFiltersAndPage(
      { sort_direction: sortDirection === 'asc' ? 'desc' : 'asc' },
      0,
    );
  };

  return (
    <Stack spacing={3}>
      <Box
        p={4}
        borderRadius="12px"
        border="1px solid"
        borderColor={borderColor}
        bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
        mb={3}
      >
        <Stack spacing={3}>
          <Stack spacing={4} direction={{ base: 'column', lg: 'row' }}>
            <FormControl flex="1">
              <FormLabel color={textColor}>Driver</FormLabel>
              <Input
                variant="main"
                placeholder="Driver name or licence"
                value={driverInputValue}
                onChange={(e) => setDriverInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="md"
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel color={textColor}>Carrier</FormLabel>
              <Input
                variant="main"
                placeholder="Carrier name"
                value={carrierInputValue}
                onChange={(e) => setCarrierInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="md"
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel color={textColor}>Requested By</FormLabel>
              <Input
                variant="main"
                placeholder="Requesting user"
                value={requestedByInputValue}
                onChange={(e) => setRequestedByInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="md"
              />
            </FormControl>
          </Stack>

          <Stack spacing={4} direction={{ base: 'column', lg: 'row' }}>
            <FormControl flex="1">
              <FormLabel color={textColor}>Transaction Type</FormLabel>
              <SelectSearch
                options={TRANSACTION_TYPE_OPTIONS}
                value={transactionTypeFilter}
                onChange={(value) =>
                  setFilter('transaction_type', value ? String(value) : '')
                }
                placeholder="All Transaction Types"
                allowClear
              />
            </FormControl>
            <Box flex="1">
              <DateField
                label="From"
                labelMl="10px"
                date={dateFrom}
                onChange={(d: Date | null) =>
                  setFilter('date_from', d ? getDateString(d) : '')
                }
              />
            </Box>
            <Box flex="1">
              <DateField
                label="To"
                labelMl="10px"
                date={dateTo}
                onChange={(d: Date | null) =>
                  setFilter('date_to', d ? getDateString(d) : '')
                }
              />
            </Box>
          </Stack>

          <Flex justify="flex-end" gap={2}>
            <Tooltip label="Refresh">
              <IconButton
                aria-label="Refresh"
                icon={isRefreshing ? <Spinner size="sm" /> : <RepeatIcon />}
                onClick={onRefresh}
                isDisabled={isRefreshing}
                variant="outline"
                size="md"
              />
            </Tooltip>
            <Button
              onClick={handleSearch}
              leftIcon={<SearchIcon />}
              size="md"
              variant="brand"
            >
              Search
            </Button>
            <Button
              onClick={onResetFilters}
              variant="outline"
              leftIcon={<SmallCloseIcon />}
              size="md"
            >
              Reset
            </Button>
          </Flex>
        </Stack>
      </Box>

      {isLoading ? (
        <LoadingCard />
      ) : error ? (
        <ErrorAlert
          title="Failed to load recent MVR requests"
          description="Please refresh the page and try again."
        />
      ) : reports.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No recent MVR requests found.
        </Text>
      ) : (
        <>
          <Box overflowX="auto">
            <Table variant="simple" size="sm" color="gray.500">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor} color="gray.400">
                    Driver
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Carrier
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Transaction Type
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Product Type
                  </Th>
                  <SortableTh
                    label="Date"
                    field="date"
                    sortField="date"
                    sortDirection={sortDirection}
                    onSort={toggleSortDirection}
                    borderColor={borderColor}
                  />
                  <Th borderColor={borderColor} color="gray.400">
                    Requested By
                  </Th>
                  <Th borderColor={borderColor} color="gray.400">
                    Status
                  </Th>
                  <Th
                    borderColor={borderColor}
                    color="gray.400"
                    textAlign="center"
                  >
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {reports.map((report) => {
                  const hasErrorTooltip =
                    !!report.error_message &&
                    (report.status === 'failed' ||
                      report.status === 'no_match');

                  return (
                    <Tr key={report.mvr_request_id}>
                      <Td>
                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                          {report.name}
                        </Text>
                        {report.license_number && (
                          <Text fontSize="xs" color="gray.500">
                            {report.license_number}
                          </Text>
                        )}
                      </Td>
                      <Td color={textColor}>{report.carrier.name}</Td>
                      <Td color={textColor}>
                        {TRANSACTION_TYPE_LABELS[report.transaction_type]}
                      </Td>
                      <Td color={textColor}>
                        {report.product_type
                          ? PRODUCT_TYPE_LABELS[report.product_type]
                          : '—'}
                      </Td>
                      <Td color={textColor}>
                        {new Date(report.created_at).toLocaleDateString()}
                      </Td>
                      <Td color={textColor}>{report.requested_by ?? '—'}</Td>
                      <Td>
                        <Tooltip
                          label={report.error_message}
                          isDisabled={!hasErrorTooltip}
                        >
                          <span>
                            <MVRStatusBadge
                              status={report.status}
                              isDuplicate={report.is_duplicate}
                            />
                          </span>
                        </Tooltip>
                      </Td>
                      <Td textAlign="center">
                        {report.pdf_available && (
                          <MvrDownloadButton
                            mvrOrderId={report.mvr_order_id}
                            mvrRequestId={report.mvr_request_id}
                            licenseNumber={report.license_number}
                            iconOnly
                          />
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          <Flex justify="center">
            <Paginate page={page} count={count} onSetPage={onSetPage} />
          </Flex>
        </>
      )}
    </Stack>
  );
};

export default MvrRequestReportList;
